import { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Stack, Divider, IconButton, Tooltip
} from '@mui/material';
import { Add, Remove, History, Refresh, PictureAsPdf, Delete } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function IngresosEgresos() {
  const [tab, setTab] = useState(0);
  const [productos, setProductos] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Formulario multi-producto
  const [lineas, setLineas] = useState([crearLineaVacia()]);
  const [bodegaId, setBodegaId] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ultimoMovimiento, setUltimoMovimiento] = useState(null);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  function crearLineaVacia() {
    return { id: Date.now() + Math.random(), producto: null, cantidad: '', costoUnitario: '' };
  }

  useEffect(() => {
    cargarProductos();
    cargarBodegas();
    cargarHistorial();
  }, []);

  const cargarProductos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/productos?tipo=PRODUCTO`, { headers: getHeaders() });
      setProductos(Array.isArray(res.data) ? res.data : []);
    } catch { setProductos([]); }
  };

  const cargarBodegas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bodegas`, { headers: getHeaders() });
      const data = res.data?.data || res.data || [];
      setBodegas(Array.isArray(data) ? data : []);
    } catch { setBodegas([]); }
  };

  const cargarHistorial = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/kardex-inventario?limit=50`, { headers: getHeaders() });
      setMovimientos(res.data?.data || []);
    } catch { setMovimientos([]); }
    setLoading(false);
  };

  const limpiarForm = () => {
    setLineas([crearLineaVacia()]);
    setBodegaId('');
    setObservaciones('');
    setMotivo('');
  };

  // ── Manejo de líneas multi-producto ──────────────────────────────────────
  const agregarLinea = () => setLineas(prev => [...prev, crearLineaVacia()]);

  const eliminarLinea = (id) => {
    setLineas(prev => prev.length > 1 ? prev.filter(l => l.id !== id) : prev);
  };

  const actualizarLinea = (id, campo, valor) => {
    setLineas(prev => prev.map(l => l.id === id ? { ...l, [campo]: valor } : l));
  };

  // ── Registrar movimiento (ingreso o egreso) ─────────────────────────────
  const registrar = async (tipo) => {
    const lineasValidas = lineas.filter(l => l.producto && parseFloat(l.cantidad) > 0);
    if (lineasValidas.length === 0) {
      return setSnackbar({ open: true, message: 'Agregue al menos un producto con cantidad', severity: 'error' });
    }
    if (tipo === 'egreso' && !motivo) {
      return setSnackbar({ open: true, message: 'Seleccione un motivo para el egreso', severity: 'error' });
    }

    setSaving(true);
    const endpoint = tipo === 'ingreso' ? 'ingreso' : 'egreso';
    let lastId = null;
    let errores = 0;

    for (const linea of lineasValidas) {
      try {
        const body = {
          productoId: linea.producto.id,
          bodegaId: bodegaId || 'PRINCIPAL',
          cantidad: parseFloat(linea.cantidad),
          costoUnitario: parseFloat(linea.costoUnitario || linea.producto.costo_ultima_compra || 0),
          observaciones,
        };
        if (tipo === 'egreso') body.motivo = motivo;

        const res = await axios.post(
          `${API_URL}/api/kardex-inventario/${endpoint}`,
          body,
          { headers: getHeaders() }
        );
        lastId = res.data?.data?.id || lastId;
      } catch {
        errores++;
      }
    }

    if (lastId) setUltimoMovimiento(lastId);
    const ok = lineasValidas.length - errores;
    setSnackbar({
      open: true,
      message: errores === 0
        ? `${ok} producto(s) ${tipo === 'ingreso' ? 'ingresado' : 'egresado'}(s) correctamente`
        : `${ok} OK, ${errores} error(es)`,
      severity: errores === 0 ? 'success' : 'warning',
    });

    limpiarForm();
    cargarHistorial();
    cargarProductos();
    setSaving(false);
  };

  const verPDF = (movimientoId) => {
    fetch(`${API_URL}/api/kardex-inventario/movimiento/${movimientoId}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.blob())
      .then(blob => { const url = URL.createObjectURL(blob); window.open(url, '_blank'); })
      .catch(() => setSnackbar({ open: true, message: 'Error al abrir PDF', severity: 'error' }));
  };

  const fmtFecha = (f) => f
    ? new Date(f).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  // ── Render del formulario ────────────────────────────────────────────────
  const renderFormulario = (tipo) => (
    <Box sx={{ mt: 2 }}>
      {/* Bodega + Motivo */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Bodega</InputLabel>
          <Select value={bodegaId} label="Bodega" onChange={(e) => setBodegaId(e.target.value)}>
            <MenuItem value="">Bodega Principal</MenuItem>
            {bodegas.map(b => (
              <MenuItem key={b.id} value={b.id}>
                {b.nombre}{b.es_principal ? ' (Principal)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {tipo === 'egreso' && (
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Motivo *</InputLabel>
            <Select value={motivo} label="Motivo *" onChange={(e) => setMotivo(e.target.value)}>
              <MenuItem value="MERMA">Merma / Deterioro</MenuItem>
              <MenuItem value="PERDIDA">Pérdida</MenuItem>
              <MenuItem value="USO_INTERNO">Uso Interno</MenuItem>
              <MenuItem value="DEVOLUCION_PROVEEDOR">Devolución a Proveedor</MenuItem>
              <MenuItem value="OBSEQUIO">Obsequio / Muestra</MenuItem>
              <MenuItem value="OTRO">Otro</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>

      {/* Tabla de líneas (multi-producto) */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box sx={{ p: 1.5, bgcolor: tipo === 'ingreso' ? '#e8f5e9' : '#ffebee' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Productos a {tipo === 'ingreso' ? 'ingresar' : 'egresar'}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '45%' }}><strong>Producto</strong></TableCell>
                <TableCell sx={{ width: '15%' }}><strong>Stock actual</strong></TableCell>
                <TableCell sx={{ width: '15%' }}><strong>Cantidad *</strong></TableCell>
                <TableCell sx={{ width: '15%' }}><strong>Costo Unit.</strong></TableCell>
                <TableCell sx={{ width: '10%' }} align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineas.map((linea) => (
                <TableRow key={linea.id}>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={productos}
                      getOptionLabel={(o) => `${o.codigo || 'S/C'} - ${o.nombre}`}
                      value={linea.producto}
                      onChange={(_, v) => {
                        actualizarLinea(linea.id, 'producto', v);
                        if (v && tipo === 'ingreso') {
                          actualizarLinea(linea.id, 'costoUnitario', String(v.costo_ultima_compra || 0));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Buscar producto..." variant="outlined" />
                      )}
                      isOptionEqualToValue={(o, v) => o?.id === v?.id}
                      disableClearable={false}
                    />
                  </TableCell>
                  <TableCell>
                    {linea.producto
                      ? <Chip label={parseFloat(linea.producto.cantidad_stock || 0)} size="small" variant="outlined" />
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={linea.cantidad}
                      onChange={(e) => actualizarLinea(linea.id, 'cantidad', e.target.value)}
                      inputProps={{ min: 0.01, step: 0.01 }}
                      sx={{ width: '100%' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={linea.costoUnitario}
                      onChange={(e) => actualizarLinea(linea.id, 'costoUnitario', e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: '100%' }}
                      placeholder="0.00"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="error" onClick={() => eliminarLinea(linea.id)}
                      disabled={lineas.length === 1}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 1.5 }}>
          <Button size="small" startIcon={<Add />} onClick={agregarLinea}>
            Agregar producto
          </Button>
        </Box>
      </Paper>

      {/* Observaciones */}
      <TextField
        fullWidth
        label="Observaciones"
        value={observaciones}
        onChange={(e) => setObservaciones(e.target.value)}
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      {/* Resumen */}
      {lineas.some(l => l.producto && l.cantidad) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{lineas.filter(l => l.producto && parseFloat(l.cantidad) > 0).length}</strong> producto(s) seleccionado(s)
          — Total unidades: <strong>{lineas.reduce((s, l) => s + (parseFloat(l.cantidad) || 0), 0).toFixed(2)}</strong>
        </Alert>
      )}

      {/* Botones */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          size="large"
          disabled={saving}
          color={tipo === 'ingreso' ? 'success' : 'error'}
          startIcon={tipo === 'ingreso' ? <Add /> : <Remove />}
          onClick={() => registrar(tipo)}
          sx={{ px: 4, py: 1.5 }}
        >
          {saving ? 'Registrando...' : tipo === 'ingreso' ? 'REGISTRAR INGRESO' : 'REGISTRAR EGRESO'}
        </Button>

        {ultimoMovimiento && (
          <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={() => verPDF(ultimoMovimiento)}>
            Ver Comprobante PDF
          </Button>
        )}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Ingresos y Egresos de Inventario
        </Typography>

        <Tabs value={tab} onChange={(_, v) => { setTab(v); setUltimoMovimiento(null); }} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<Add />} iconPosition="start" label="INGRESO" />
          <Tab icon={<Remove />} iconPosition="start" label="EGRESO" />
          <Tab icon={<History />} iconPosition="start" label="HISTORIAL" />
        </Tabs>

        {tab === 0 && renderFormulario('ingreso')}
        {tab === 1 && renderFormulario('egreso')}
        {tab === 2 && (
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={cargarHistorial} sx={{ mb: 2 }}>
              Actualizar
            </Button>
            {loading ? (
              <Box textAlign="center" py={4}><CircularProgress /></Box>
            ) : movimientos.length === 0 ? (
              <Alert severity="info">No hay movimientos registrados.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Fecha</strong></TableCell>
                      <TableCell><strong>Tipo</strong></TableCell>
                      <TableCell><strong>Producto</strong></TableCell>
                      <TableCell><strong>Bodega</strong></TableCell>
                      <TableCell align="right"><strong>Cantidad</strong></TableCell>
                      <TableCell align="right"><strong>Costo U.</strong></TableCell>
                      <TableCell><strong>Observaciones</strong></TableCell>
                      <TableCell align="center"><strong>PDF</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movimientos.map((m) => (
                      <TableRow key={m.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtFecha(m.fecha)}</TableCell>
                        <TableCell>
                          <Chip
                            label={m.tipo}
                            size="small"
                            color={m.tipo === 'ENTRADA' ? 'success' : m.tipo === 'SALIDA' ? 'error' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>{m.producto?.nombre || '—'}</TableCell>
                        <TableCell>{m.bodega?.nombre || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: m.tipo === 'SALIDA' ? 'error.main' : 'success.main' }}>
                          {m.tipo === 'SALIDA' ? '-' : '+'}{m.cantidad}
                        </TableCell>
                        <TableCell align="right">${parseFloat(m.costoUnitario || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {m.observaciones || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver comprobante PDF">
                            <IconButton size="small" color="error" onClick={() => verPDF(m.id)}>
                              <PictureAsPdf fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
